import { Tooltip } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import { AccountingProvider, accountingIdentifiersFor } from '../../library/constants/accounting-provider.js';
import { ExactOnlineIcon, PeppolIcon, TwinfieldIcon, XeroIcon } from './custom-icons.jsx';

const ICONS = {
    [AccountingProvider.TWINFIELD]: TwinfieldIcon,
    [AccountingProvider.EXACT]: ExactOnlineIcon,
    [AccountingProvider.XERO]: XeroIcon,
}

export const AccountingIdentifierIcon = ({ provider, style }) => {
    const Component = ICONS[provider]
    return Component ? <Component style={style} /> : null
}
AccountingIdentifierIcon.propTypes = {
    provider: PropTypes.string.isRequired,
    style: PropTypes.object,
}

/** Compact icon row for a panel header; only shows identifiers that exist. */
export const AccountingIdentifierIcons = ({ record, entityId, identifiers }) => (
    <>
        {accountingIdentifiersFor(record, entityId, { identifiers })
            .filter(({ value }) => !!value)
            .map(({ key, label, value, secondaryLabel, secondaryValue }) => (
                <React.Fragment key={key}>
                    <Tooltip title={`${label}: ${value}`}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', marginLeft: '4px' }}>
                            <AccountingIdentifierIcon provider={key} />
                        </span>
                    </Tooltip>
                    {!!secondaryValue && (
                        <Tooltip title={`${secondaryLabel}: ${secondaryValue}`}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', marginLeft: '4px' }}>
                                <PeppolIcon />
                            </span>
                        </Tooltip>
                    )}
                </React.Fragment>
            ))}
    </>
)
AccountingIdentifierIcons.propTypes = {
    record: PropTypes.object,
    entityId: PropTypes.number,
    identifiers: PropTypes.object,
}

/** Full rows for a details pane: expected providers show "Not synced" when blank, unexpected ones are marked legacy. */
export const AccountingIdentifierFields = ({ record, entityId, identifiers, Wrapper }) => (
    <>
        {accountingIdentifiersFor(record, entityId, { identifiers }).map(({ key, label, value, expected, secondaryLabel, secondaryValue }) => (
            <Wrapper key={key} label={label}>
                {value ? (
                    <Tooltip title={`${label}: ${value}`}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                            <AccountingIdentifierIcon provider={key} />
                            <span>{value}</span>
                        </span>
                    </Tooltip>
                ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', opacity: 0.45 }}>
                        <AccountingIdentifierIcon provider={key} />
                        <span>Not synced</span>
                    </span>
                )}
                {!!secondaryValue && (
                    <Tooltip title={`${secondaryLabel}: ${secondaryValue}`}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: '8px', cursor: 'pointer' }}>
                            <PeppolIcon />
                            <span>{secondaryValue}</span>
                        </span>
                    </Tooltip>
                )}
                {!expected && (
                    <Tooltip title="An identifier from a system this entity no longer books in — a record created before the entity moved, or one that changed entity. Shown because it is still a real booking.">
                        <span style={{ marginLeft: '6px', opacity: 0.45 }}>(legacy)</span>
                    </Tooltip>
                )}
            </Wrapper>
        ))}
    </>
)
AccountingIdentifierFields.propTypes = {
    record: PropTypes.object,
    entityId: PropTypes.number,
    identifiers: PropTypes.object,
    Wrapper: PropTypes.elementType.isRequired,
}
